async function readResponse(response, fallback) {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(payload.error || payload.message || fallback);
        error.status = response.status;
        error.data = payload;
        throw error;
    }
    return {
        data: payload.data,
        meta: payload.meta || {},
        etag: response.headers.get('ETag'),
    };
}

export function createContentApi({fetch, apiUrl = ''}) {
    const root = `${apiUrl}/v1/seller`;
    const collection = kind => kind === 'blog' ? 'blogs' : 'pages';
    const collectionUrl = kind => `${root}/${collection(kind)}`;
    const url = (kind, uuid, suffix = '') => (
        `${collectionUrl(kind)}/${encodeURIComponent(uuid)}${suffix}`
    );
    const mutation = (method, requestUrl, revision, body, fallback, extraHeaders = {}) => fetch(requestUrl, {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...(revision ? {'If-Match': revision} : {}),
            ...extraHeaders,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    }).then(response => readResponse(response, fallback));

    return {
        async list(kind, page = 1, limit = 25) {
            const response = await fetch(
                `${collectionUrl(kind)}?editorVersion=2&page=${page}&limit=${limit}`,
            );
            return readResponse(response, 'Unable to load content.');
        },
        create(kind, values) {
            return mutation(
                'POST', `${collectionUrl(kind)}?editorVersion=2`, null, values,
                `Unable to create this ${kind === 'blog' ? 'blog post' : 'page'}.`,
            );
        },
        async load(kind, uuid) {
            const response = await fetch(url(kind, uuid, '/editor'));
            return readResponse(response, 'Unable to load this content.');
        },
        saveDraft(kind, uuid, revision, draft, intent = 'autosave') {
            return mutation('PATCH', url(kind, uuid, '/draft'), revision, draft,
                'Unable to save this draft.', {'X-Content-Save-Intent': intent});
        },
        convert(kind, uuid, revision, values) {
            return mutation('POST', url(kind, uuid, '/convert'), revision, values,
                'Unable to preview this conversion.');
        },
        preview(kind, uuid, revision, locale) {
            return mutation('POST', url(kind, uuid, '/preview-token'), revision, {locale},
                'Unable to create a preview.');
        },
        publish(kind, uuid, revision) {
            return mutation('POST', url(kind, uuid, '/publish'), revision, {},
                'Unable to publish this content.');
        },
        unpublish(kind, uuid, revision) {
            return mutation('POST', url(kind, uuid, '/unpublish'), revision, {},
                'Unable to unpublish this content.');
        },
        requestRedReview(uuid, revision) {
            return mutation('POST', url('blog', uuid, '/red-review'), revision, {},
                'Unable to request Mayhem RED review.');
        },
        announce(uuid, revision, preamble) {
            return mutation('POST', url('blog', uuid, '/announce'), revision, {preamble},
                'Unable to announce this blog post.');
        },
        async loadComments(uuid, page = 1, limit = 25) {
            const response = await fetch(url(
                'blog', uuid, `/comments?page=${page}&limit=${limit}`,
            ));
            return readResponse(response, 'Unable to load comments.');
        },
        replyToComment(uuid, commentUuid, reply) {
            return mutation(
                'POST',
                url('blog', uuid, `/comments/${encodeURIComponent(commentUuid)}/reply`),
                null,
                {reply},
                'Unable to reply to this comment.',
            );
        },
        setCommentVisibility(uuid, commentUuid, visible) {
            return mutation(
                'PATCH',
                url(
                    'blog',
                    uuid,
                    `/comments/${encodeURIComponent(commentUuid)}/${visible ? 'show' : 'hide'}`,
                ),
                null,
                {},
                'Unable to update comment visibility.',
            );
        },
        gallery(kind, uuid, revision, method, suffix = '', body) {
            return mutation(method, url(kind, uuid, `/gallery${suffix}`), revision, body,
                'Unable to update the gallery.');
        },
        galleryOrder(kind, uuid, revision, order) {
            return mutation('PUT', url(kind, uuid, '/gallery-order'), revision, {order},
                'Unable to reorder the gallery.');
        },
        youtube(kind, uuid, revision, method, suffix = '', body) {
            return mutation(method, url(kind, uuid, `/youtube${suffix}`), revision, body,
                'Unable to update YouTube videos.');
        },
        youtubeOrder(kind, uuid, revision, order) {
            return mutation('PUT', url(kind, uuid, '/youtube-order'), revision, {order},
                'Unable to reorder YouTube videos.');
        },
        saveTranslation(kind, uuid, locale, revision, body) {
            return mutation(
                'PUT', url(kind, uuid, `/translations/${encodeURIComponent(locale)}`),
                revision, body, 'Unable to save this translation.',
            );
        },
        deleteTranslation(kind, uuid, locale, revision) {
            return mutation(
                'DELETE', url(kind, uuid, `/translations/${encodeURIComponent(locale)}`),
                revision, undefined, 'Unable to remove this translation.',
            );
        },
        deleteContent(kind, uuid, revision) {
            return mutation('DELETE', url(kind, uuid, '/editor'), revision, undefined,
                'Unable to delete this content.');
        },
    };
}

export default createContentApi;
